/**
 * Simple fallacy analysis service
 * Direct edge function calls with confidence normalization
 */

import { supabase } from '@/integrations/supabase/client';
import type { FallacyAnalysis } from '@/types/fallacyAnalysis';
import type { FlippedResponse } from '@/types/flipped';

/**
 * Normalize confidence from 0-1 to 0-100 scale
 */
function normalizeConfidence(confidence: number): number {
  return confidence <= 1 ? Math.round(confidence * 100) : Math.round(confidence);
}

/**
 * Analyze text for logical fallacies
 */
export async function analyzeFallacies(text: string): Promise<FallacyAnalysis> {
  const { data, error } = await supabase.functions.invoke('analyze-text', {
    body: { text },
  });

  if (error) throw new Error(error.message);
  if (!data) throw new Error('No response from service');

  // Normalize confidence values
  return {
    ...data,
    confidence: normalizeConfidence(data.confidence),
    fallacies: data.fallacies?.map((f: any) => ({
      ...f,
      confidence: normalizeConfidence(f.confidence),
    })),
  };
}

/**
 * Generate constructive counter-approach for a fallacy
 */
export async function generateFlippedApproach(fallacy: {
  name: string;
  severity: string;
  category: string;
  description: string;
  examples: string[];
  alternativeNames: string[];
}): Promise<FlippedResponse> {
  const { data, error } = await supabase.functions.invoke('generate-flipped-approach', {
    body: { fallacy },
  });

  if (error) throw new Error(error.message);
  if (!data) throw new Error('No response from service');

  return data;
}
